import { useState, useRef, useEffect, useCallback, useMemo, type FormEvent } from 'react';
import type React from 'react';
import { Bot, Globe, GraduationCap, MessageSquare, PanelRight, Paperclip, Camera, FolderPlus, Folder, ChevronRight, Plus, ArrowUp, ArrowLeft, ArrowDown, Users, X, Square, RotateCcw } from 'lucide-react';
import { Virtuoso, type VirtuosoHandle, type ListProps, type Components } from 'react-virtuoso';
import { SkeletonMessage } from '../../core/ui/skeleton';
import { useToastContext } from '../../core/ui/toast/ToastProvider';
import SurfaceHeader from '../../core/layout/SurfaceHeader';
import MessageBubble from './MessageBubble';
import TypingDots from './TypingDots';
import type { Message, Professional } from '../../types';
import type { TabType } from '../../core/navigation/useNavigationState';
import chiefyMascot from '../../assets/images/ChiefyMascot.png';
import { isChatQuotaExhausted } from '../../userService';
import ChatQuotaBanner from './ChatQuotaBanner';
import { useChatAutoScroll } from './useChatAutoScroll';
import { useComposerSize } from './useComposerSize';
import './ChatRoom.css';

/** Custom Virtuoso slot components. Defined at module scope so they
 *  keep stable identity across renders (re-creating them per render
 *  would force Virtuoso to remount its scroller). The `List` slot
 *  carries our existing `.messages-list` class so module CSS targeting
 *  it still applies.
 *
 *  React 19: `ref` is a regular prop — `forwardRef` has been removed.
 *  Virtuoso's `ListProps` type passes a required `ref`; typing the
 *  component as `ListProps` (not `React.HTMLAttributes`) matches the
 *  Components slot contract exactly. */
function VirtuosoList({ ref, children, style }: ListProps) {
    return (
        <div ref={ref} style={style} className="messages-list">
            {children}
        </div>
    );
}

// Composer-clearance lives entirely in CSS — see the SSOT block on
// `.messages-last-slot` in ChatRoom.css. The matching ResizeObserver-
// driven auto-pin lives in useChatAutoScroll.

/** Starter prompts shown on a new/empty chat so users aren't stuck at
 *  a blank page. Phrased in the product's voice (property development
 *  focus) and short enough to read at a glance. */
const STARTER_PROMPTS = [
    'Draft a feasibility summary for a 20-lot subdivision',
    'Explain the zoning rules I should check before buying in NSW',
    'Review this land plan and flag site constraints',
    'Help me structure a development-application cover letter',
] as const;

type ChatPartner = {
    name: string;
    avatar: string;
    online: boolean;
};

type GroupChatInfo = {
    name: string;
    memberCount: number;
    members: Professional[];
};

type ChatRoomProps = {
    messages: Message[];
    placeholder: string;
    title: string;
    subtitle?: string | undefined;
    isLoadingMessages?: boolean | undefined;
    activeTab: TabType;
    onMenuClick: () => void;
    showMenu: boolean;
    onTogglePanel?: (() => void) | undefined;
    isPanelOpen?: boolean | undefined;
    onBack?: (() => void) | undefined;
    chatPartner?: ChatPartner | undefined;
    groupChat?: GroupChatInfo | undefined;
    chatId?: string | undefined;
    projectId?: string | undefined;
    projects?: { id: string; name: string }[] | undefined;
    onMoveToProject?: ((projectId: string | null) => void) | undefined;
    onCreateProject?: (() => void) | undefined;
    onSendMessage?: ((content: string, file?: File | null) => Promise<void>) | undefined;
    userData?: import('../../userService').BackendUserWithPlan | null | undefined;
    /** Streaming state machine — used to gate Stop/Retry/Regenerate buttons. */
    chatState?: import('./useStreamingChat').ChatState | undefined;
    /** Aborts the current streaming request. No-op if nothing is in flight. */
    onStop?: (() => void) | undefined;
    /** Re-sends the last user turn. Used after cancel / error. */
    onRetry?: (() => Promise<void>) | undefined;
    /** Asks the server to regenerate the last assistant turn in place. */
    onRegenerate?: (() => Promise<void>) | undefined;
};

function ChatRoom({
    messages,
    placeholder,
    title,
    subtitle,
    isLoadingMessages = false,
    activeTab,
    onMenuClick,
    showMenu,
    onTogglePanel,
    isPanelOpen,
    onBack,
    chatPartner,
    groupChat,
    chatId,
    projectId,
    projects = [],
    onMoveToProject,
    onCreateProject,
    onSendMessage,
    userData,
    chatState,
    onStop,
    onRetry,
    onRegenerate,
}: ChatRoomProps) {
    const toast = useToastContext();
    const [input, setInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const virtuosoRef = useRef<VirtuosoHandle | null>(null);
    // Virtuoso owns the scroll container and exposes it via the
    // `scrollerRef` callback. Tracked in state so scroll-fade re-
    // attaches cleanly when Virtuoso re-mounts on `key={chatId}`
    // change — a plain ref wouldn't trigger the effect below.
    const [scrollerEl, setScrollerEl] = useState<HTMLElement | null>(null);
    // Stable identity — Virtuoso's scrollerRef is called once per
    // scroller mount; no need to recreate the callback per render.
    const attachMessagesArea = useCallback((node: HTMLElement | Window | null) => {
        const el = node && node !== window ? (node as HTMLElement) : null;
        setScrollerEl(el);
    }, []);

    // Scroll-fade wire — inlined rather than delegated to `useScrollFade`
    // because that hook was built for a stable-lifetime ref, whereas
    // Virtuoso re-mounts the scroller on chat switch. Re-runs cleanly
    // whenever `scrollerEl` changes so the mask-image gradient follows
    // the new element.
    useEffect(() => {
        const el = scrollerEl;
        if (!el) return;
        el.classList.add('scroll-fade');
        el.dataset.scrollFadeAxis = 'y';
        el.dataset.scrollFadeSize = 'lg';
        const THRESHOLD = 1;
        let pending = false;
        let frame = 0;
        const measure = () => {
            pending = false;
            const { scrollTop, scrollHeight, clientHeight } = el;
            el.dataset.fadeTop = scrollTop > THRESHOLD ? 'on' : 'off';
            el.dataset.fadeBottom =
                scrollTop + clientHeight < scrollHeight - THRESHOLD ? 'on' : 'off';
        };
        const schedule = () => {
            if (pending) return;
            pending = true;
            frame = requestAnimationFrame(measure);
        };
        measure();
        el.addEventListener('scroll', schedule, { passive: true });
        const ro = new ResizeObserver(schedule);
        ro.observe(el);
        const firstChild = el.firstElementChild;
        if (firstChild) ro.observe(firstChild);
        return () => {
            if (frame !== 0) cancelAnimationFrame(frame);
            el.removeEventListener('scroll', schedule);
            ro.disconnect();
            el.classList.remove('scroll-fade');
            delete el.dataset.scrollFadeAxis;
            delete el.dataset.scrollFadeSize;
            delete el.dataset.fadeTop;
            delete el.dataset.fadeBottom;
        };
    }, [scrollerEl]);
    const [panelTooltip, setPanelTooltip] = useState<{ top: number; left: number } | null>(null);
    const [attachedFile, setAttachedFile] = useState<File | null>(null);
    const [showScrollBtn, setShowScrollBtn] = useState(false);
    const [showAttachMenu, setShowAttachMenu] = useState(false);
    const [showProjectSubmenu, setShowProjectSubmenu] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const attachMenuRef = useRef<HTMLDivElement>(null);
    const stackRef = useRef<HTMLDivElement>(null);

    // Composer measurement → publishes `--composer-h` to the
    // .chatroom__stack so .messages-last-slot's padding-bottom calc
    // (and the ↓-button's `bottom`) track the dock's real height.
    // Single concern, fully encapsulated — see useComposerSize.ts.
    // We only need the ref-callback in this component; the hook also
    // exposes the live composer ref for callers that want a one-off
    // measurement, but everything here reads via the CSS var.
    const { attachComposer } = useComposerSize();

    const handleFileClick = () => {
        fileInputRef.current?.click();
    };

    const handleScreenshot = async () => {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
            const track = stream.getVideoTracks()[0];
            if (!track) return;
            const canvas = document.createElement('canvas');
            const video = document.createElement('video');
            video.srcObject = stream;
            await video.play();
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext('2d')?.drawImage(video, 0, 0);
            track.stop();
            canvas.toBlob((blob) => {
                if (blob) {
                    if (blob.size > 3_932_160) {
                        toast.error(`Screenshot is too large (${(blob.size / (1024 * 1024)).toFixed(1)} MB) — maximum is 3.9 MB. Try capturing a smaller area.`);
                        return;
                    }
                    const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
                    setAttachedFile(file);
                }
            }, 'image/png');
        } catch {
            // User cancelled or browser doesn't support
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (!file) {
                return;
            }
            
            if (file.size > 3_932_160) {
                toast.error(`File is too large. '${file.name}' is ${(file.size / (1024 * 1024)).toFixed(1)} MB — maximum is 3.9 MB.`);
                e.target.value = '';
                return;
            }
            setAttachedFile(file);
        }
    };

    const removeFile = () => {
        setAttachedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const showPanelTooltip = (e: React.MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setPanelTooltip({ top: rect.top + rect.height / 2, left: rect.left - 12 });
    };
    const hidePanelTooltip = () => setPanelTooltip(null);
    const handlePanelToggle = () => {
        hidePanelTooltip();
        onTogglePanel?.();
    };

    const hasStreamingAssistantMessage = messages.some(
        (m) => m.role === 'assistant' && m.streaming,
    );

    // Pre-compute the index of the last completed assistant turn so each
    // row can decide regenerate-button visibility in O(1) instead of
    // O(n) (the previous .map() called `messages.slice(idx + 1).some(...)`
    // on every row). Semantics match exactly: if the most-recent
    // assistant is streaming, no row gets the button (you can't
    // regenerate an in-flight turn).
    const lastAssistantIdx = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            const m = messages[i];
            if (m && m.role === 'assistant') {
                return m.streaming ? -1 : i;
            }
        }
        return -1;
    }, [messages]);

    // Show the typing-dots placeholder during the gap between
    // turn-start and the first streamed assistant chunk arriving.
    //
    // Two paths reach this state:
    //   - Form submit:     `isSubmitting` (local) flips to true in
    //                      handleSubmit; chatState may not yet have
    //                      moved to 'submitting' on the same tick.
    //   - Regenerate:      bypasses handleSubmit entirely and goes
    //                      through useStreamingChat.regenerate(),
    //                      which sets `chatState='submitting'` on the
    //                      hook side. The local isSubmitting STAYS
    //                      false because the form was never submitted
    //                      — that's why the placeholder previously
    //                      didn't render during regen.
    // Either signal is sufficient; we OR them so both paths converge
    // on the same UI state. `!hasStreamingAssistantMessage` guards
    // against double-rendering once the first chunk arrives.
    const isInFlight = isSubmitting || chatState === 'submitting';
    const showThinkingPlaceholder =
        isInFlight && Boolean(onSendMessage) && !hasStreamingAssistantMessage;

    const quotaExhausted = isChatQuotaExhausted(userData);
    const isInputDisabled = isSubmitting || isLoadingMessages || quotaExhausted;

    // Auto-scroll concern → fully encapsulated in useChatAutoScroll.
    // The hook owns three paths:
    //   1. Initial pin on mount + chat switch (useLayoutEffect, paint-tight).
    //   2. In-flight auto-pin via ResizeObserver on the last-slot div.
    //      Fires on EVERY layout change (per-token growth, markdown
    //      shifts, --composer-h updates), so the contract — messages
    //      never touch the composer except when user manually scrolls —
    //      is held deterministically rather than via Virtuoso's
    //      heuristic auto-tail (which can go stale under bursts).
    //   3. Imperative scrollToBottom for the ↓ button.
    //
    // Composer-clearance is owned ENTIRELY by `.messages-last-slot`'s
    // padding-bottom calc in ChatRoom.css — see the SSOT block there.
    // The hook passes NO `offset` to scrollToIndex; the slot's natural
    // measured height carries the clearance and Virtuoso's
    // `align: 'end'` lands at the right visual position.
    const { scrollToBottom, setLastSlotEl, onAtBottomStateChange } = useChatAutoScroll({
        virtuosoRef,
        chatId,
        isLoadingMessages,
        chatState,
        messagesLength: messages.length,
    });

    // Virtuoso `components` prop. Memoised on `showThinkingPlaceholder`
    // so the scroller doesn't remount per keystroke — the boolean only
    // flips on submit / first-stream-frame / after-done (at most twice
    // per turn), which is cheap. When thinking, a Footer renders the
    // TypingDots placeholder; otherwise the slot wrapper applied below
    // in itemContent is the only clearance carrier.
    //
    // ThinkingFooterBound is bound here (rather than at module scope)
    // so it can attach `setLastSlotEl` to its slot wrapper — when the
    // placeholder is the visual last item, useChatAutoScroll's
    // ResizeObserver needs the same hook into its size as a real
    // message slot.
    const ThinkingFooterBound = useCallback(
        () => (
            <div ref={setLastSlotEl} className="messages-last-slot">
                <TypingDots />
            </div>
        ),
        [setLastSlotEl],
    );
    const virtuosoComponents = useMemo<Components<Message>>(
        () =>
            showThinkingPlaceholder
                ? { List: VirtuosoList, Footer: ThinkingFooterBound }
                : { List: VirtuosoList },
        [showThinkingPlaceholder, ThinkingFooterBound],
    );

    // Close attach menu on click outside
    useEffect(() => {
        if (!showAttachMenu) return;
        const handler = (e: MouseEvent) => {
            if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) {
                setShowAttachMenu(false);
                setShowProjectSubmenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showAttachMenu]);

    // Esc = Stop generating while a stream is in flight.
    useEffect(() => {
        if (!onStop) return;
        if (chatState !== 'streaming' && chatState !== 'submitting') return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onStop();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [chatState, onStop]);

    // Scroll-to-bottom button visibility is driven from Virtuoso's
    // `atBottomStateChange`. Per-token re-pin lives in
    // useChatAutoScroll's ResizeObserver. Length-change auto-tail
    // (new turns appended) is Virtuoso's native `followOutput="auto"`.

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        const hasContent = input.trim() || attachedFile;
        if (!hasContent || isInputDisabled) return;

        const messageContent = input.trim();
        const fileToUpload = attachedFile;

        setInput('');
        setAttachedFile(null);
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setIsSubmitting(true);
        // Deliberately NO scrollToBottom here. At this exact tick the
        // optimistic user message hasn't been appended yet (it's added
        // inside `onSendMessage`'s setMessages call below), so a scroll
        // here would target the OLD last item — typically the previous
        // assistant message — and the user would watch their existing
        // history slide downward in a smooth animation before their
        // own typed message appeared.
        //
        // useChatAutoScroll's defence re-pin (useLayoutEffect, fires
        // when messagesLength changes during 'submitting') lands the
        // scroll on the optimistic user the moment it's appended, in
        // the SAME paint as the new message — no visible misalignment.

        try {
            if (onSendMessage) {
                await onSendMessage(messageContent, fileToUpload);
            }
        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get tab-specific icon
    const getTabIcon = () => {
        switch (activeTab) {
            case 'assistant': return <Bot size={18} />;
            case 'networking': return <Globe size={18} />;
            case 'education': return <GraduationCap size={18} />;
            default: return <MessageSquare size={18} />;
        }
    };

    // Check if this is a DM chat (has chatPartner) or group chat
    const isDmChat = !!chatPartner;
    const isGroupChat = !!groupChat;

    // Compose DM / group / default title in the title slot so the
    // avatar + name visual survives the chrome migration. Subtitle is
    // handled natively via SurfaceHeader's subtitle prop for DM/group
    // (muted second line matches the `.header-subtitle` style).
    const renderedIcon = !isDmChat && !isGroupChat ? getTabIcon() : undefined;
    const renderedTitle: React.ReactNode = isGroupChat && groupChat ? (
        <div className="chatroom-header-title chatroom-header-title--group">
            <span className="group-avatar-header"><Users size={16} /></span>
            <span className="chatroom-header-title__name">{title}</span>
        </div>
    ) : isDmChat && chatPartner ? (
        <div className="chatroom-header-title chatroom-header-title--dm">
            <span className={`dm-avatar ${chatPartner.online ? 'online' : ''}`}>
                {chatPartner.avatar}
                {chatPartner.online && <span className="status-indicator" />}
            </span>
            <span className="chatroom-header-title__name">{title}</span>
        </div>
    ) : (
        <span className="chatroom-header-title chatroom-header-title--default">
            <span className="chatroom-header-title__name">{title}</span>
            {projectId && (
                <span className="header-project-label">
                    <Folder size={12} />
                    {projects.find(p => p.id === projectId)?.name || 'Project'}
                </span>
            )}
        </span>
    );

    const panelToggleAction = onTogglePanel ? (
        <button
            type="button"
            className={`action-btn panel-toggle ${isPanelOpen ? 'active' : ''}`}
            onClick={handlePanelToggle}
            onMouseEnter={showPanelTooltip}
            onMouseLeave={hidePanelTooltip}
            aria-label={isPanelOpen ? 'Close panel' : 'Open panel'}
            aria-pressed={Boolean(isPanelOpen)}
        >
            <PanelRight size={18} />
        </button>
    ) : null;

    return (
        <div className={`chatroom ${messages.length === 0 && !isLoadingMessages && !showThinkingPlaceholder ? 'chatroom--empty' : ''}`}>
            <SurfaceHeader
                title={renderedTitle}
                {...(renderedIcon ? { icon: renderedIcon } : {})}
                {...(subtitle && (isDmChat || isGroupChat) ? { subtitle } : {})}
                onMenuClick={onMenuClick}
                showMenu={showMenu}
                {...(onBack ? { onBack, backLabel: 'Go back' } : {})}
                actions={panelToggleAction}
                onHeaderClick={() => virtuosoRef.current?.scrollToIndex({ index: 0, align: 'start', behavior: 'smooth' })}
            />

            {/* Overlay stack: messages, empty-state, ↓-button, and the
              *  composer share one grid cell so the frosted glass pill
              *  overlays the scroller's bottom edge — messages scroll
              *  UNDER the composer, giving backdrop-filter real content
              *  to blur. The composer's real height is measured on mount
              *  (attachComposer ref callback) and on resize (RO above),
              *  written as --composer-h, and consumed by the in-scroller
              *  spacer + ↓-button offset in ChatRoom.css.
              *
              *  Messages Area — three states:
              *  1. Loading historical messages → skeleton list
              *  2. Empty thread → welcome / starter prompts
              *  3. Has messages → virtualized via Virtuoso so a long
              *     thread renders only the visible window of bubbles
              *     (DOM node count drops from O(n) to O(visible+overscan)).
              *     `followOutput="auto"` covers length-change auto-tail
              *     (new turns appended). Per-token / per-paint re-pin
              *     during streaming lives in useChatAutoScroll's
              *     ResizeObserver on the .messages-last-slot div, which
              *     respects manual scroll-up via the userScrolledAway
              *     flag the hook tracks from atBottomStateChange.
              */}
            <div
                className={
                    messages.length === 0 && !showThinkingPlaceholder
                        ? 'chatroom__stack chatroom__stack--empty'
                        : 'chatroom__stack'
                }
                ref={stackRef}
            >
            {isLoadingMessages ? (
                <div className="messages-area" aria-busy="true">
                    <div className="messages-list">
                        <SkeletonMessage align="right" />
                        <SkeletonMessage align="left" />
                        <SkeletonMessage align="right" />
                        <SkeletonMessage align="left" />
                    </div>
                </div>
            ) : messages.length === 0 && !showThinkingPlaceholder ? (
                // Non-assistant tabs render their own empty state here.
                // The assistant tab's welcome (mascot + starter prompts)
                // lives in the `.empty-welcome` block below — rendering
                // both would duplicate the welcome heading.
                activeTab !== 'assistant' ? (
                    <div className="messages-area">
                        <div className="empty-state">
                            <div className="empty-icon">{getTabIcon()}</div>
                            <h2>Start a conversation</h2>
                            <p>Type a message below to get started</p>
                        </div>
                    </div>
                ) : null
            ) : (
                <Virtuoso
                    key={chatId ?? 'empty'}
                    ref={virtuosoRef}
                    className="messages-area"
                    aria-live="polite"
                    aria-relevant="additions"
                    data={messages}
                    scrollerRef={attachMessagesArea}
                    // `align: 'end'` docks the BOTTOM of the last message
                    // at the BOTTOM of the viewport on mount — matches
                    // chat-app convention (read the latest turn in full,
                    // scroll up for history). Plain numeric index would
                    // dock the TOP of the last message at the top of the
                    // viewport instead, cutting off long replies.
                    //
                    // No `offset`: composer-clearance is owned by
                    // `.messages-last-slot`'s padding-bottom calc in
                    // ChatRoom.css (see SSOT block there). The slot's
                    // bottom edge sits at the scroller-viewport bottom
                    // (under the composer overlay by design), and the
                    // padding lifts the bubble's visible content above
                    // the composer top.
                    initialTopMostItemIndex={{
                        index: 'LAST',
                        align: 'end',
                    }}
                    // followOutput="auto" handles length-change auto-tail
                    // (new turns appended). Per-token / per-paint re-pins
                    // are owned by useChatAutoScroll's ResizeObserver on
                    // the .messages-last-slot div — that's the path that
                    // holds the contract under bursts, where Virtuoso's
                    // own atBottomState heuristic can drift stale.
                    followOutput="auto"
                    /* The slot's padding-bottom (composer_h + ~56px) puts
                     * the natural-bottom scroll position roughly 200px
                     * below the visible composer top, so a user who's
                     * "visually at the bottom" can already be ~200px
                     * from Virtuoso's measured maxScroll. With the
                     * default 200px threshold the ↓ button flickers
                     * visible on the slightest scroll-up gesture; 600px
                     * gives a comfortable buffer so the button only
                     * appears when the user has genuinely scrolled
                     * upward to read history. */
                    atBottomThreshold={600}
                    atBottomStateChange={(atBottom) => {
                        // Hook needs the raw flip to track whether the
                        // user has manually scrolled away (suppress
                        // window filters out our own programmatic scrolls).
                        onAtBottomStateChange(atBottom);
                        setShowScrollBtn(!atBottom);
                    }}
                    components={virtuosoComponents}
                    increaseViewportBy={{ top: 600, bottom: 600 }}
                    itemContent={(idx, message) => {
                        // Regenerate lives on the *last* assistant turn —
                        // see the `lastAssistantIdx` useMemo above.
                        const isLastAssistant = idx === lastAssistantIdx;
                        const hasContent = !!message.content?.trim();
                        const inFlight =
                            chatState === 'streaming' || chatState === 'submitting';
                        const canRegenerate =
                            isLastAssistant && hasContent && !inFlight && !!onRegenerate;
                        // `.messages-last-slot` wraps the row that's the
                        // true visual last (no ThinkingFooter below it).
                        // The `--streaming` modifier bumps padding while
                        // the bubble is streaming. ref={setLastSlotEl}
                        // hands the wrapper to useChatAutoScroll for the
                        // ResizeObserver-driven auto-pin. See ChatRoom.css
                        // SSOT block for clearance values.
                        const isTrueLast =
                            !showThinkingPlaceholder && idx === messages.length - 1;
                        const isStreamingLast = isTrueLast && Boolean(message.streaming);
                        const bubble = (
                            <MessageBubble
                                message={message}
                                onRegenerate={canRegenerate ? onRegenerate : undefined}
                            />
                        );
                        return isTrueLast ? (
                            // ref={setLastSlotEl} hands the slot div to
                            // useChatAutoScroll, which observes its
                            // size with a ResizeObserver and re-pins on
                            // every layout change while in-flight.
                            <div
                                ref={setLastSlotEl}
                                className={
                                    isStreamingLast
                                        ? 'messages-last-slot messages-last-slot--streaming'
                                        : 'messages-last-slot'
                                }
                            >
                                {bubble}
                            </div>
                        ) : (
                            bubble
                        );
                    }}
                />
            )}

            {/* Scroll to bottom button. 'smooth' gives the user a
              *  visual confirmation that the action took effect; the
              *  hook gates the RO-driven auto-pin for the duration of
              *  the smooth animation (smoothScrollInProgressRef) so a
              *  per-token RO tick can't interrupt with an instant
              *  scrollToIndex mid-animation. */}
            {showScrollBtn && messages.length > 0 && (
                <button
                    type="button"
                    className="scroll-to-bottom-btn"
                    onClick={() => scrollToBottom('smooth')}
                    aria-label="Scroll to bottom"
                >
                    <ArrowDown size={18} />
                </button>
            )}

            {/* Welcome content — shown above input when empty. First-chat
             *  anchor: mascot + starter prompts so the user isn't staring
             *  at a blank page wondering what Chiefy can do. */}
            {messages.length === 0 && !isLoadingMessages && !showThinkingPlaceholder && activeTab === 'assistant' && (
                <div className="empty-welcome">
                    <img src={chiefyMascot} alt="Chiefy" className="chiefy-mascot" />
                    <h2 className="welcome-title">Welcome — I'm Chiefy</h2>
                    <p className="welcome-subtitle">Ask me anything to get started.</p>
                    <div className="welcome-starters" role="list">
                        {STARTER_PROMPTS.map((p) => (
                            <button
                                key={p}
                                type="button"
                                role="listitem"
                                className="welcome-starter"
                                onClick={() => {
                                    setInput(p);
                                    // Focus the composer so the user can
                                    // edit before sending, rather than
                                    // auto-submitting (respect agency).
                                    textareaRef.current?.focus();
                                }}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Retry — only after error/cancel. Stop is now the send-button
             *  icon swap (see below); Regenerate lives next to Copy on the
             *  last assistant bubble. */}
            {(chatState === 'error' || chatState === 'cancelled') && onRetry && (
                <div className="chat-turn-controls">
                    <button
                        type="button"
                        className="chat-turn-btn"
                        onClick={() => { void onRetry(); }}
                    >
                        <RotateCcw size={14} />
                        <span>{chatState === 'error' ? 'Retry' : 'Try again'}</span>
                    </button>
                </div>
            )}

            {/* Dock: composer form + AI disclosure move as one visual
             *  unit. The dock is what aligns to the bottom of the grid
             *  cell (overlay mode) OR the last flex item (empty mode),
             *  so the disclosure is always glued directly below the
             *  composer regardless of layout state. Its ref feeds the
             *  --composer-h measurement so `.messages-last-slot`
             *  clearance reserves enough space for composer AND
             *  disclosure — no message content hides behind either. */}
            <div className="chatroom__dock" ref={attachComposer}>
            {/* Chat quota pre-warn — fires at 80% consumption (Phase G6)
             *  so the user sees the wall coming instead of hitting the
             *  paywall modal mid-conversation. Rendered inside the
             *  dock so it stacks with the composer. */}
            <form className="input-area" onSubmit={handleSubmit}>
                <ChatQuotaBanner />
                <div className="input-container">
                    {/* Chips: project badge + file attachment */}
                    {(projectId && !messages.length || attachedFile) && (
                        <div className="input-chips">
                            {projectId && !messages.length && (
                                <div className="input-chip input-chip--project">
                                    <Folder size={14} />
                                    <span>{projects.find(p => p.id === projectId)?.name || 'Project'}</span>
                                    <button type="button" className="input-chip-remove" onClick={() => onMoveToProject?.(null)} title="Remove from project">
                                        <X size={12} />
                                    </button>
                                </div>
                            )}
                            {attachedFile && (
                                <div className="input-chip input-chip--file">
                                    <Paperclip size={14} />
                                    <span>{attachedFile.name}</span>
                                    <button type="button" className="input-chip-remove" onClick={removeFile} title="Remove attachment">
                                        <X size={12} />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="chatroom__file-input"
                        aria-hidden="true"
                    />
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => {
                            setInput(e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (input.trim() && !isInputDisabled) {
                                    (e.target as HTMLTextAreaElement).form?.requestSubmit();
                                }
                            }
                        }}
                        placeholder={placeholder}
                        disabled={isInputDisabled}
                        className="chat-input"
                        rows={1}
                    />
                    <div className="input-bottom-row">
                        <div className="input-actions-left">
                            <div className="attach-menu-container" ref={attachMenuRef}>
                                <button
                                    type="button"
                                    className={`input-action attach-toggle ${showAttachMenu ? 'active' : ''}`}
                                    title="Add content"
                                    onClick={() => setShowAttachMenu(!showAttachMenu)}
                                >
                                    <Plus size={18} />
                                </button>
                                {showAttachMenu && (
                                    <div className="attach-menu">
                                        {!showProjectSubmenu ? (
                                            <>
                                                <button type="button" className="attach-menu-item" onClick={() => { handleFileClick(); setShowAttachMenu(false); }}>
                                                    <Paperclip size={18} />
                                                    <span>Add files or photos</span>
                                                </button>
                                                {typeof navigator?.mediaDevices?.getDisplayMedia === 'function' && (
                                                    <button type="button" className="attach-menu-item" onClick={() => { handleScreenshot(); setShowAttachMenu(false); }}>
                                                        <Camera size={18} />
                                                        <span>Take a screenshot</span>
                                                    </button>
                                                )}
                                                <div className="attach-menu-divider" />
                                                <button type="button" className="attach-menu-item" onClick={() => setShowProjectSubmenu(true)}>
                                                    <FolderPlus size={18} />
                                                    <span>{projectId ? 'Move to project' : 'Add to project'}</span>
                                                    <ChevronRight size={14} className="attach-menu-chevron" />
                                                </button>
                                            </>
                                        ) : (
                                            <div className="attach-submenu">
                                                <button type="button" className="attach-menu-item attach-menu-back" onClick={() => setShowProjectSubmenu(false)}>
                                                    <ArrowLeft size={16} />
                                                    <span>Back</span>
                                                </button>
                                                <div className="attach-menu-divider" />
                                                {projectId && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="attach-menu-item"
                                                            onClick={() => { onMoveToProject?.(null); setShowAttachMenu(false); setShowProjectSubmenu(false); }}
                                                        >
                                                            <X size={16} />
                                                            <span>Remove from project</span>
                                                        </button>
                                                        <div className="attach-menu-divider" />
                                                    </>
                                                )}
                                                {projects.filter(p => p.id !== projectId).map(p => (
                                                    <button
                                                        key={p.id}
                                                        type="button"
                                                        className="attach-menu-item"
                                                        onClick={() => { onMoveToProject?.(p.id); setShowAttachMenu(false); setShowProjectSubmenu(false); }}
                                                    >
                                                        <Folder size={16} />
                                                        <span>{p.name}</span>
                                                    </button>
                                                ))}
                                                <div className="attach-menu-divider" />
                                                <button type="button" className="attach-menu-item attach-menu-create" onClick={() => { onCreateProject?.(); setShowAttachMenu(false); setShowProjectSubmenu(false); }}>
                                                    <Plus size={16} />
                                                    <span>Start new project</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        {chatState === 'streaming' || chatState === 'submitting' ? (
                            // Swap the send affordance for a Stop affordance while
                            // a turn is in flight — same pixel position, same
                            // enabled state, just a different semantic action.
                            // Must be type=button so it doesn't submit the form.
                            <button
                                type="button"
                                className="send-btn send-btn--stop"
                                onClick={() => onStop?.()}
                                disabled={!onStop}
                                aria-label="Stop generating response"
                                title="Stop generating"
                            >
                                <Square size={14} fill="currentColor" />
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className="send-btn"
                                disabled={!input.trim() || isInputDisabled}
                                aria-label="Send message"
                                title="Send message"
                            >
                                <ArrowUp size={16} />
                            </button>
                        )}
                    </div>
                </div>
            </form>

            {/* Footer — Claude-style: centered disclosure below input.
             *  Inside the dock so it travels with the composer; the
             *  dock's ref-measured height includes this disclosure,
             *  keeping --composer-h accurate and the last-message
             *  clearance correct. */}
            {activeTab === 'assistant' && (
                <div className="chat-footer">
                    <p className="ai-disclosure">
                        Chiefy is AI and can make mistakes. Please double-check responses.
                    </p>
                </div>
            )}
            </div>{/* /.chatroom__dock */}
            </div>{/* /.chatroom__stack */}

            {/* Panel toggle tooltip — position passed via CSS custom
              *  properties (`--tooltip-top` / `--tooltip-left`) on the
              *  element, read by `.panel-toggle-tooltip`'s `top` / `left`
              *  rules. Values are dynamic (from the trigger's bounding
              *  rect) so they can't live in a static class. */}
            {panelTooltip && (
                <div
                    className="panel-toggle-tooltip"
                    style={{
                        ['--tooltip-top' as string]: `${panelTooltip.top}px`,
                        ['--tooltip-left' as string]: `${panelTooltip.left}px`,
                    } as React.CSSProperties}
                >
                    {isPanelOpen ? 'Close panel' : 'Open panel'}
                </div>
            )}
        </div>
    );
}

export default ChatRoom;
